// Validation for the JWT share tokens that gate the three public pages (customer profile, eSIM
// installation, Shopify profile). Each of them used to carry its own copy of this, and two of
// those copies rejected any token without an `exp` claim.
//
// The server issues exactly such tokens on purpose: ShareTokenService.generateShareToken only
// calls setExpiration when the configured validity is non-zero, so a validity of 0 days means
// "never expires" and the claim is simply absent. Rejecting those is wrong, and on the customer
// profile it is actively bad - the failure path shows a toast and then reloads, so the page
// reloads forever.

export interface ShareTokenPayload {
    sub?: string;
    exp?: number; // absent when the token was issued without an expiry
}

// JWT segments are base64url, which swaps `+/` for `-_` and drops the padding. Handing that
// straight to atob() throws on any payload that happens to contain one of the swapped
// characters, which would look like an invalid token rather than a decoding slip.
function decodeBase64Url(segment: string): string {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - (base64.length % 4)) % 4;
    return atob(base64 + "=".repeat(padding));
}

export function parseShareToken(token: string): ShareTokenPayload | null {
    const segments = token.split(".");
    if (segments.length < 2 || !segments[1]) return null;
    try {
        const payload = JSON.parse(decodeBase64Url(segments[1]));
        return payload && typeof payload === "object" ? payload : null;
    } catch (e) {
        console.log("[shareToken] Could not parse share token: ", e);
        return null;
    }
}

/**
 * Whether the token is well-formed and, if it carries an expiry, still inside it. Signature
 * verification is the server's job - it re-checks the token on every call this page makes, so
 * this is only here to avoid firing a request that is certain to fail.
 */
export function isValidShareToken(token: string | (string | null)[] | null | undefined): boolean {
    if (Array.isArray(token) || !token) return false;
    const payload = parseShareToken(token);
    if (!payload) return false;
    if (payload.exp === undefined || payload.exp === null) return true;
    if (typeof payload.exp !== "number" || !isFinite(payload.exp)) return false;
    return Date.now() <= payload.exp * 1000;
}
